/**
 * Who is signed in, and what the market is doing.
 *
 * Two contexts that every layout needs and that nothing else should fetch
 * twice: the session, and the market clock. The clock is one interval for
 * the whole document, so twenty validity clocks on a board tick together and
 * freeze together rather than drifting a few hundred milliseconds apart.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { ReactNode } from 'react'
import {
  isSessionOpen,
  sessionSecondsBetween,
  sessionSecondsRemaining,
  sessionState,
} from '@metal/shared'
import { ApiError, api, onSessionExpired, request, tokens } from './api'

export type MembershipStatus =
  | 'basic'
  | 'limited'
  | 'pending'
  | 'active'
  | 'suspended'
  | 'rejected'

export interface SessionUser {
  partyId: string
  partyNumber: number
  /** The party's name: the company for a company member, the person otherwise. */
  displayName: string
  shortName: string
  /**
   * The account holder's name, as the user row carries it. For a member who
   * registered a company through the full form this is the company name, the
   * same as `displayName`; otherwise it is the person. The account block shows
   * the person and the company, never a role or a grade, and prints the
   * company line only when it differs from this one.
   */
  personName: string
  /** The avatar's letter, as the party or the staff member chose it. */
  avatarLetter: string | null
  /** A staff member's job title. Null for a member. */
  titleFa: string | null
  mobile: string
  partyKind: 'person' | 'company'
  membership: MembershipStatus
  sides: Array<'buy' | 'sell'>
  grade: 'gold' | 'silver' | 'bronze' | 'none' | null
  gradeScore: number | null
  province: string | null
  roles: string[]
  permissions: string[]
  operator: boolean
  /** Server step of the onboarding file, when it is unfinished. */
  onboardingStep: number | null
  /**
   * The person who registered the account. A colleague a company admin
   * added is not, and does not complete the company's file.
   */
  isPrimary: boolean
  unreadNotifications: number
  /** A staff member still on a password a manager handed over; the console opens nothing else. */
  mustSetPassword: boolean
}

/* ==========================================================================
   The wire, and the one place it becomes a principal
   ========================================================================== */

/** `GET /auth/session`: who is signed in and what they were granted. */
interface SessionView {
  user: {
    userNo: number
    displayName: string
    platformRole: string | null
    titleFa?: string | null
    avatarLetter?: string | null
    mustSetPassword?: boolean
  }
  party: { partyNo: number; kind: 'PERSON' | 'COMPANY'; status: string; tradingRole: string | null }
  companyPermissions: string[]
  platformDomains: Record<string, string> | null
  /** The keys the server's own guards read, expanded by the server. */
  permissions?: string[]
}

/** `GET /me`: the party's own file, which is where the card and the province live. */
interface MeView {
  user: { fullName: string; mobile: string; isPrimary?: boolean } | null
  party: {
    partyId: string
    partyNo: number
    kind: 'PERSON' | 'COMPANY'
    displayName: string
    shortName?: string
    avatarLetter?: string | null
    membership: MembershipStatus
    province: string | null
    tradingRole: 'BUYER' | 'SELLER' | 'BOTH' | null
    grade: 'GOLD' | 'SILVER' | 'BRONZE' | 'NONE' | null
    gradeScore: number | null
  }
  kycCase: { step: number } | null
}

/**
 * Three reads become one principal.
 *
 * The server splits what a screen needs about the viewer across the session
 * (who they are and what they were granted), their own file (the card, the
 * province, the side they trade) and the notification centre (the pip). Every
 * screen wants one object, and the composition is the same every time, so it
 * happens once, here, and nothing else in the client calls these three.
 */
function principalOf(
  session: SessionView,
  me: MeView | null,
  unread: number,
): SessionUser {
  const operator = session.user.platformRole !== null
  const tradingRole = me?.party.tradingRole ?? session.party.tradingRole
  // Every member trades both ways; the recorded role only decides which side
  // leads (the API's sidesOf says the same).
  const sides: Array<'buy' | 'sell'> =
    tradingRole === 'BUYER'
      ? ['buy', 'sell']
      : tradingRole === 'SELLER' || tradingRole === 'BOTH'
        ? ['sell', 'buy']
        : []
  const membership = me?.party.membership ?? 'basic'
  const name = me?.party.displayName ?? session.user.displayName
  const grade = me?.party.grade ?? null

  return {
    partyId: me?.party.partyId ?? '',
    partyNumber: me?.party.partyNo ?? session.party.partyNo,
    displayName: name,
    shortName: me?.party.shortName ?? name,
    personName: me?.user?.fullName || session.user.displayName,
    // Staff sign in under the platform's own party, so their letter is their own.
    avatarLetter: (operator ? session.user.avatarLetter : me?.party.avatarLetter) ?? null,
    titleFa: session.user.titleFa ?? null,
    mobile: me?.user?.mobile ?? '',
    partyKind: (me?.party.kind ?? session.party.kind) === 'PERSON' ? 'person' : 'company',
    membership,
    sides,
    grade: grade === null ? null : (grade.toLowerCase() as SessionUser['grade']),
    gradeScore: me?.party.gradeScore ?? null,
    province: me?.party.province ?? null,
    roles: session.user.platformRole ? [session.user.platformRole] : [],
    // The server expands the grant into the very keys its guards check, so
    // the client asks the same question rather than inventing its own
    // vocabulary out of the domains. A locally derived set would hide
    // operator screens the server would have opened.
    permissions:
      session.permissions ??
      (operator
        ? Object.entries(session.platformDomains ?? {})
            .filter(([, level]) => level !== 'none')
            .flatMap(([domain, level]) => [domain, `${domain}:${level}`])
        : session.companyPermissions),
    operator,
    // Only an unfinished file has a step to go back to. An active member has
    // none, and the guard would otherwise send them into the wizard.
    onboardingStep: membership === 'active' ? null : (me?.kycCase?.step ?? null),
    isPrimary: me?.user?.isPrimary ?? true,
    unreadNotifications: unread,
    mustSetPassword: session.user.mustSetPassword ?? false,
  }
}

interface SessionContextValue {
  user: SessionUser | null
  loading: boolean
  /**
   * The server could not be reached when the session was last read, and no
   * principal is known: not signed out, only not answered. A guard shows a
   * retry rather than the sign in.
   */
  unreachable: boolean
  /** Re-reads the principal, after activation or a role change. */
  reload: () => Promise<void>
  signOut: () => Promise<void>
  setAccessToken: (token: string) => Promise<void>
  can: (permission: string) => boolean
  isSeller: boolean
  isBuyer: boolean
}

const SessionContext = createContext<SessionContextValue | null>(null)

export function SessionProvider({
  children,
  scope = 'member',
  onIdentityChange,
}: {
  children: ReactNode
  /**
   * Called whenever the person behind the session may change: a sign out, a
   * sign in, a lapsed session, or a read that comes back as somebody else.
   * The app drops every cached answer there, so nobody sees the last
   * person's data.
   */
  onIdentityChange?: () => void
  /**
   * The operator console reads the session alone: staff have no file of
   * their own and no notification pip, so the two member reads are skipped.
   */
  scope?: 'member' | 'operator'
}) {
  const [user, setUser] = useState<SessionUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [unreachable, setUnreachable] = useState(false)
  const lastParty = useRef<string | null>(null)
  const reset = useRef(onIdentityChange)
  reset.current = onIdentityChange

  const load = useCallback(async () => {
    try {
      const session = await api.get<SessionView>('/auth/session')
      setUnreachable(false)
      if (scope === 'operator') {
        const next = principalOf(session, null, 0)
        if (lastParty.current && lastParty.current !== next.partyId) reset.current?.()
        lastParty.current = next.partyId
        setUser(next)
        return
      }
      // The file and the pip are not what says whether somebody is signed in,
      // so neither one failing signs them out.
      const [me, summary] = await Promise.all([
        api.get<MeView>('/me').catch(() => null),
        api.get<{ unread: number }>('/notifications/summary').catch(() => null),
      ])
      const next = principalOf(session, me, summary?.unread ?? 0)
      if (lastParty.current && lastParty.current !== next.partyId) reset.current?.()
      lastParty.current = next.partyId
      setUser(next)
    } catch (e) {
      // Only a refusal signs somebody out. A server that could not be
      // reached, or a proxy answering for one that is down (a 5xx), says
      // nothing about the session: the principal stays as it was, and the
      // next read asks again.
      const offline = e instanceof ApiError && (e.status === 0 || e.status >= 500)
      setUnreachable(offline)
      if (!offline) setUser(null)
    } finally {
      setLoading(false)
    }
  }, [scope])

  useEffect(() => {
    void load()
  }, [load])

  // A lapsed session is a signed out one: the guards send a protected page to
  // the sign in form, and the toast says why, instead of leaving a page whose
  // every query now fails.
  useEffect(
    () =>
      onSessionExpired(() => {
        lastParty.current = null
        reset.current?.()
        setUser(null)
      }),
    [],
  )

  const signOut = useCallback(async () => {
    await api.post('/auth/session/logout').catch(() => undefined)
    tokens.set(null)
    lastParty.current = null
    reset.current?.()
    setUser(null)
  }, [])

  /**
   * The token is what a sign in produces; the principal is read back from the
   * server, because the verify reply carries a name and a number and nothing
   * a guard can decide on.
   */
  const setAccessToken = useCallback(
    async (token: string) => {
      tokens.set(token)
      // A fresh sign in starts from nothing cached.
      lastParty.current = null
      reset.current?.()
      setLoading(true)
      await load()
    },
    [load],
  )

  const value = useMemo<SessionContextValue>(
    () => ({
      user,
      loading,
      unreachable,
      reload: load,
      signOut,
      setAccessToken,
      can: (permission: string) => Boolean(user?.permissions?.includes(permission)),
      isSeller: Boolean(user?.sides?.includes('sell')),
      isBuyer: Boolean(user?.sides?.includes('buy')),
    }),
    [user, loading, unreachable, load, signOut, setAccessToken],
  )

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext)
  if (!ctx) throw new Error('useSession must be used inside SessionProvider')
  return ctx
}

/* ==========================================================================
   The market clock
   ========================================================================== */

export interface MarketClock {
  /** The server's idea of now, corrected for client skew. */
  now: Date
  open: boolean
  closesAt: Date | null
  opensAt: Date | null
  /**
   * Seconds of session time left until `expiresAt`. Returns the frozen value
   * while the market is shut, because validity does not burn outside the
   * window. Computed from the server clock, never from the device clock.
   */
  remaining: (expiresAt: Date | string) => number
  /**
   * Session seconds burned since `from`. A list route sends the seconds left
   * at the instant it answered rather than the instant the thing expires, so
   * a cell carries that count forward itself: `sent - elapsedSince(asOf)`.
   */
  elapsedSince: (from: Date | string) => number
  frozen: boolean
}

const MarketClockContext = createContext<MarketClock | null>(null)

export function MarketClockProvider({ children }: { children: ReactNode }) {
  const skew = useRef(0)
  const [tick, setTick] = useState(() => Date.now())

  useEffect(() => {
    let cancelled = false
    const sync = async () => {
      const started = Date.now()
      try {
        // Anonymous: this tick runs on a timer, and a timer must not renew a
        // session nobody is using.
        const res = await request<{ now: string }>('/market/clock', { anonymous: true })
        if (cancelled) return
        const roundTrip = Date.now() - started
        const server = new Date(res.now).getTime() + roundTrip / 2
        skew.current = server - Date.now()
      } catch {
        // A clock we cannot sync is the device clock. Worse, but not fatal.
      }
    }
    void sync()
    const resync = window.setInterval(sync, 5 * 60 * 1000)
    return () => {
      cancelled = true
      window.clearInterval(resync)
    }
  }, [])

  useEffect(() => {
    const id = window.setInterval(() => setTick(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [])

  const value = useMemo<MarketClock>(() => {
    const now = new Date(tick + skew.current)
    const state = sessionState(now)
    return {
      now,
      open: state.open,
      closesAt: state.closesAt,
      opensAt: state.opensAt,
      frozen: !isSessionOpen(now),
      remaining: (expiresAt) => {
        const target = expiresAt instanceof Date ? expiresAt : new Date(expiresAt)
        return sessionSecondsRemainingSafe(target, now)
      },
      elapsedSince: (from) => {
        const start = from instanceof Date ? from : new Date(from)
        if (Number.isNaN(start.getTime())) return 0
        return sessionSecondsBetween(start, now)
      },
    }
  }, [tick])

  return <MarketClockContext.Provider value={value}>{children}</MarketClockContext.Provider>
}

export function useMarketClock(): MarketClock {
  const ctx = useContext(MarketClockContext)
  if (!ctx) throw new Error('useMarketClock must be used inside MarketClockProvider')
  return ctx
}

/** One implementation of the freeze rule, shared with the API. */
function sessionSecondsRemainingSafe(expiresAt: Date, now: Date): number {
  if (Number.isNaN(expiresAt.getTime())) return 0
  return Math.max(0, sessionSecondsRemaining(expiresAt, now))
}
